import React, { useState, useRef, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
	Container,
	Row,
	Col,
	Button,
	ToastContainer,
	Toast,
} from 'react-bootstrap';
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

//  Styles
import './MessageForm.scss';

//  API
import useAdminData from '../../hooks/useAdminData';

//  Components
import { SocialIcons } from '../SocialIcons';
import { TextInput, RadioButton, RadioButtonGroup, TextArea } from '../Inputs';
import Spinner from '../Spinner';

gsap.registerPlugin(ScrollTrigger);

const ContactForm = () => {
	const [state, setState] = useState({ name: '', show: false });
	const { isLoading, sendMessage } = useAdminData();
	const textRefs = useRef([]);
	textRefs.current = [];

	const addToTextRefs = el => {
		if (el && !textRefs.current.includes(el)) {
			textRefs.current.push(el);
		}
	};

	function calcDelay(index) {
		if (index === 0) return index;
		return index - index * 0.8;
	}

	useEffect(() => {
		textRefs.current.forEach((el, index) => {
			gsap.fromTo(
				el,
				{ autoAlpha: 0, y: 50 },
				{
					autoAlpha: 1,
					y: 0,
					duration: 1,
					delay: calcDelay(index),
					ease: 'power4.out',
					scrollTrigger: {
						id: 'projetos',
						trigger: el,
						start: 'top center+=100',
					},
				}
			);
		});
	}, []);

	return (
		<Formik
			initialValues={{
				name: '',
				email: '',
				subject: '',
				text: '',
			}}
			validationSchema={Yup.object({
				name: Yup.string()
					.max(40, '⚠️ Must have at most 40 characters')
					.min(2, '⚠️ Must have at least 2 characters')
					.required('⚠️ Mandatory'),
				email: Yup.string()
					.email('⚠️ Invalid email')
					.required('⚠️ Mandatory'),
				text: Yup.string()
					.min(10, '⚠️ Must have at least 15 characters')
					.max(200, '⚠️ Must have at most 200 characters')
					.required('⚠️ Mandatory'),
				subject: Yup.string().required('⚠️ Have to choose one subject'),
			})}
			onSubmit={async (values, { setSubmitting, resetForm }) => {
				await sendMessage(values).then(result => {
					setState({ name: values.name, show: true });
					setSubmitting(false);
					resetForm();
				});
			}}>
			{formik => (
				<Form className="mb-5 mt-4">
					<div className="top-form m-4">
						<Container>
							<Row>
								<Col className="text-center">
									<h1 className="title" ref={addToTextRefs}>
										How can I help you?
									</h1>
									<p className="subtitle text-center" ref={addToTextRefs}>
										Any questions? Send me a message!
									</p>
								</Col>
							</Row>
						</Container>
					</div>
					<div className="bottom-form" ref={addToTextRefs}>
						<Container>
							<div className="form-box jumbotron jumbotron-fluid bg-light">
								<Container fluid>
									<Row>
										<Col md={5} className="p-0">
											<div className="box h-100 bg-dark-blue p-4 position-relative">
												<p className="text-white fw-bold text-start">
													Contact Info
												</p>
												<p className="text-white fw-light text-start">
													Fill out the form and you
													will get an answer, in less
													than 24 hours.
												</p>
												<section className="mt-2 mb-4">
													<p className="text-white my-1">
														<i className="fas fa-phone-alt text-danger me-2"></i>{' '}
														+351 9366 6950 65
													</p>
													<p className="text-white">
														<i className="fas fa-envelope text-danger me-2"></i>{' '}
														franciscomrs53@gmail.com
														<br />
														<i className="fas fa-envelope text-danger me-2"></i>{' '}
														francisco.r.e.silva@tecnico.ulisboa.pt
													</p>
												</section>
												<section className="social-media-buttons">
													<SocialIcons />
												</section>
											</div>
										</Col>
										<Col md={7}>
											<Container className="py-3">
												<Row className="mb-3">
													<Col xs={12} md={6}>
														<TextInput
															label="Name"
															name="name"
															type="text"
															placeholder="Name"
														/>
													</Col>
													<Col xs={12} md={6}>
														<TextInput
															label="Email"
															name="email"
															type="email"
															placeholder="Email"
														/>
													</Col>
													<Col xs={12}>
														<RadioButtonGroup
															id="checkboxGroup"
															label="What is the subject?"
															value={
																formik.values
																	.subject
															}
															error={
																formik.errors
																	.subject
															}
															touched={
																formik.touched
																	.subject
															}
															onChange={
																formik.setFieldValue
															}
															onBlur={
																formik.setFieldTouched
															}>
															<Field
																component={
																	RadioButton
																}
																name="subject"
																id="Website"
																label="Website"
															/>
															<Field
																component={
																	RadioButton
																}
																name="subject"
																id="Partnership"
																label="Partnership"
															/>
															<Field
																component={
																	RadioButton
																}
																name="subject"
																id="Bug"
																label="Bug"
															/>
															<Field
																component={
																	RadioButton
																}
																name="subject"
																id="Other"
																label="Other"
															/>
														</RadioButtonGroup>
													</Col>
												</Row>
												<Row>
													<Col>
														<TextArea
															label="Message"
															name="text"
															type="text"
															placeholder="Write your message ..."
														/>
													</Col>
												</Row>
												<Row className="text-end">
													<Col>
														<Button
															type="submit"
															variant="primary"
															disabled={
																formik.isSubmitting ||
																!formik.isValid
															}
															className="fs-10">
															Send
														</Button>
													</Col>
												</Row>
												{formik.isSubmitting &&
													isLoading && (
														<Spinner color="red" />
													)}
											</Container>
										</Col>
									</Row>
								</Container>
							</div>
						</Container>
						<ToastContainer position="bottom-end" className="p-3">
							<Toast
								onClose={() =>
									setState({ name: '', show: false })
								}
								show={state.show}
								bg="primary">
								<Toast.Header className="bg-danger text-white">
									<strong className="me-auto text-white">
										👋 - Francisco Ribeiro e Silva
									</strong>
									<small className="text-white">Now</small>
								</Toast.Header>
								<Toast.Body className="text-white">
									Hello <b>{state.name}</b>!<br />
									Thank you for your message.
									<br />I will contact you shortly.
								</Toast.Body>
							</Toast>
						</ToastContainer>
					</div>
				</Form>
			)}
		</Formik>
	);
};
export default ContactForm;
